import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Chart from 'react-google-charts';
import { API } from '../../../api/API';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import { useNavigate } from 'react-router-dom';

export const MonthComponent: React.FC<{ month: { [key: string]: string }, employeeId: number, dropdownLabel: string }> = ({ month, employeeId, dropdownLabel }) => {
  const [expenseData, setExpenseData] = useState<Array<any>>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDataForMonth = async () => {
      const serverUrl = localStorage.getItem('serverUrl');
      const monthEntries = Object.entries(month);
      const fetchedData: Array<any> = [];

      for (const [range] of monthEntries) {
        try {
          const formattedMonth = range.split('|')[0].slice(5, 7) + '-' + range.split('|')[0].slice(0, 4); // Convert to 'mm-yyyy'
          const url = `${serverUrl}${API.expenseOverview}`;
          const params = {
            employee_id: employeeId,
            moye: formattedMonth,
          };
          const { status, data } = await getData(url, true, params);

          if (status === 200) {
            fetchedData.push({ range, data: data.data });
            console.log(`Expense Overview API Response for ${range}:`, data);
          } else {
            console.error(`Failed to fetch expense overview data for ${range}.`);
          }
        } catch (error) {
          console.error(`Error fetching expense overview data for ${range}:`, error);
        }
      }

      setExpenseData(fetchedData);
    };

    fetchDataForMonth();
  }, [month, employeeId]);

  const openExpensesPage = (clickedMonth: string, totalExpenseAmount: number) => {
    navigate('/expenses', {
      state: {
        month: { [clickedMonth]: month[clickedMonth] },
        employeeId,
        dropdownLabel,
        totalExpenseAmount, // Pass TotalExpenseAmount as a new parameter
      },
    });
  };

  const renderPieCharts = (data: any, range: string) => {
    const pieChartData = [
      {
        title: 'Working',
        data: [
          ['Category', 'Value'],
          ['HQ', data.Working.TotalHQ],
          ['ExHQ', data.Working.TotalExHQ],
          ['Los', data.Working.TotalLos],
          ['Os', data.Working.TotalOs],
        ],
      },
      {
        title: 'Attendance',
        data: [
          ['Category', 'Value'],
          ['Working Days', data.Attendance.TotalWorkingDays],
          ['FiedWork', data.Attendance.TotalFiedWork],
          ['Leaves', data.Attendance.TotalLeaves],
          ['Holidays', data.Attendance.TotalHolidays],
        ],
      },
      {
        title: 'Expense',
        data: [
          ['Category', 'Value'],
          ['Expenses', data.Expense.TotalExpenseAmount],
          ['Ta', data.Expense.TotalTaAmount],
          ['Da', data.Expense.TotalDaAmount],
          ['Other', data.Expense.TotalOtherAmount],
        ],
      },
    ];

    const formattedMonth = new Date(range.split('|')[0]).toLocaleString('default', {
      month: 'short',
      year: 'numeric',
    });

    return (
      <div style={{ marginBottom: '20px', border: '1px solid #ccc', borderRadius: '8px', padding: '10px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '10px' }}>
          <h4 style={{ textAlign: 'left', margin: 0 }}>{formattedMonth}</h4>
          <h4
            style={{ textAlign: 'left', margin: 0, cursor: 'pointer', color: 'blue' }}
            onClick={() => openExpensesPage(range, data.Expense.TotalExpenseAmount)} // Pass TotalExpenseAmount here
          >
            {'View'}
          </h4>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          {pieChartData.map((chart, index) => {
            const allZero = chart.data.slice(1).every(([, value]) => value === 0);
            return (
              <div
                key={index}
                style={{
                  width: '30%',
                  borderLeft: index === 1 ? '1px solid #ddd' : 'none',
                  borderRight: index === 1 ? '1px solid #ddd' : 'none',
                  padding: index === 1 ? '5px' : '0',
                }}
              >
                <h5 style={{ textAlign: 'left' }}>{chart.title}</h5>

                <Chart
                  chartType="PieChart"
                  data={allZero ? [['Category', 'Value'], ['No Data', 1]] : chart.data}
                  options={{
                    pieHole: 0.6,
                    legend: { position: 'bottom' },
                    pieSliceText: 'none',
                    slices: allZero
                      ? { 0: { color: '#d3d3d3' } } // Grey color for no data
                      : {
                          0: { color: '#338bff' },
                          1: { color: '#1c65d3' },
                          2: { color: '#0800fc' },
                          3: { color: '#85cffd' },
                        },
                  }}
                  height="200px"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {expenseData.map(({ range, data }) => (
        <div key={range}>
          {renderPieCharts(data, range)}
        </div>
      ))}
    </div>
  );
};

const ExpenseApprovals: React.FC = () => {
    const [allowedMonths, setAllowedMonths] = useState<Array<{ [key: string]: string }> | null>(null);
    const [teamMembers, setTeamMembers] = useState<Array<{ EmployeeId: number; FirstName: string; LastName: string }> | null>(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true); // Add loading state

    useEffect(() => {
        const fetchAllowedMonths = async () => {
            try {
                const serverUrl = localStorage.getItem('serverUrl');
                const url = `${serverUrl}${API.getAllowedMonths}`;
                const { status, data } = await getData(url, true);

                if (status === 200 && Array.isArray(data.data) && data.data.length > 0) {
                    setAllowedMonths(data.data);
                    console.log('Allowed Months API Response:', data.data);
                } else {
                    console.error('Failed to fetch allowed months or no data available.');
                }
            } catch (error) {
                console.error('Error fetching allowed months:', error);
            } finally {
                setIsLoading(false); // Set loading to false after API call
            }
        };

        const fetchTeamMembers = async () => {
            try {
                const serverUrl = localStorage.getItem('serverUrl');
                const url = `${serverUrl}${API.getMyTeam}`;
                const { status, data } = await getData(url, true);

                if (status === 200 && data?.data?.team?.length > 0) {
                    setTeamMembers(data?.data?.team);
                    console.log('Team Members API Response:', data?.data?.team);
                } else {
                    console.error('Failed to fetch team members or no data available.');
                }
            } catch (error) {
                console.error('Error fetching team members:', error);
            } finally {
                setIsLoading(false); // Set loading to false after API call
            }
        };

        fetchAllowedMonths();
        fetchTeamMembers();
    }, []);

    useEffect(() => {
        if (teamMembers && teamMembers.length > 0) {
            setSelectedEmployeeId(teamMembers[0].EmployeeId);
        }
    }, [teamMembers]);

    const teamOptions = teamMembers?.map((member) => ({
        value: member.EmployeeId,
        label: `${member.FirstName} ${member.LastName}`,
    }));

    return (
        <div style={{}} className='px-3'>
            {isLoading ? (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>Loading...</div>
            ) : (
              <>
                {teamOptions && (
                  <div className='d-flex justify-content-end align-items-center' style={{ marginBottom: '10px' }}>
                    <Select
                      options={teamOptions}
                      value={teamOptions.find(option => option.value === selectedEmployeeId)}
                      onChange={(selectedOption) => setSelectedEmployeeId(selectedOption?.value || null)}
                      placeholder="Search Team "
                      isClearable
                      styles={{ container: (provided) => ({ ...provided, width: '300px' }) }}
                    />
                  </div>
                )}
                {allowedMonths && selectedEmployeeId ? (
                    allowedMonths.map((month, index) => (
                        <MonthComponent
                            key={index}
                            month={month}
                            employeeId={selectedEmployeeId}
                            dropdownLabel={teamOptions?.find(option => option.value === selectedEmployeeId)?.label || ''}
                        />
                    ))
                ) : (
                    <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>No data found</div>
                )}
              </>
            )}
        </div>
    );
};

export default ExpenseApprovals;
